/* eslint-disable dot-notation */
import {Component, OnDestroy, OnInit} from '@angular/core';
import {filter, Subscription} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {map} from 'rxjs/operators';
import {Title} from '@angular/platform-browser';
import {TITLE} from '@twpub/core/constants';
import {ClientConfigurationService} from '@twpub/core/services';
import {CssService} from '@twpub/core/services/css.service';
import {CookieConsentService} from './cookie-consent/services/cookie-consent.service';
import {NGXLogger} from 'ngx-logger';
import {
  FeedbackModeOverlayComponent
} from '@twpub/shared/components/feedback-mode-overlay/feedback-mode-overlay.component';

@Component({
  selector: 'pub-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, FeedbackModeOverlayComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private statusChangeSubscription!: Subscription;

  constructor(
    private logger: NGXLogger,
    private activatedRoute: ActivatedRoute,
    private title: Title,
    private router: Router,
    private clientConfigService: ClientConfigurationService,
    private cssService: CssService,
    private cookieService: CookieConsentService
  ) {
  }

  ngOnInit(): void {
    this.setPageTitle();
    this.loadClientConfiguration();
    this.statusChangeSubscription = this.cookieService.statusChange$.subscribe(
      (event: any) => {
        this.logger.debug('Cookie consent status changed:', event.status);
      });
  }

  ngOnDestroy(): void {
    if (this.statusChangeSubscription) {
      this.statusChangeSubscription.unsubscribe();
    }
  }

  /**
   * Sets page title to <code>data.title</code> specified in routes.
   * @see https://dev.to/nightwolfdev/dynamically-set-page-title-in-angular-app-35ca
   */
  setPageTitle(): void {
    const defaultPageTitle = TITLE;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd),
      map(() => {
        let child = this.activatedRoute.firstChild;
        if (!child) {
          return this.activatedRoute.snapshot.data['title'] || defaultPageTitle;
        }
        while (child.firstChild) {
          child = child.firstChild;
        }
        if (child.snapshot.data['title']) {
          return child.snapshot.data['title'] || defaultPageTitle;
        }
      })
    ).subscribe((title: string) => this.title.setTitle(title));
  }

  /**
   * Load client configuration and initialize fonts
   */
  private loadClientConfiguration(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const configId = urlParams.get('configId');
    const configIdNum = configId ? parseInt(configId, 10) : null;

    this.clientConfigService.getDefaultConfiguration(configIdNum)
      .subscribe(config => {
        this.cssService.loadFontUrls(config);
        this.cssService.loadFonts(config);
      });
  }
}
